import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { catalogApi } from "../api/client";
import ListingCard from "../Components/ListingCard";
import FilterSideBar from "../Components/FilterSideBar";
import { SearchX } from "lucide-react";
import SearchBar from "../Components/SearchBar";
import Navbar from "../Components/Navbar";
import HalfHero from "../Images/main.jpg";

const ServicesByCategory = () => {
  const { categoryId } = useParams();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [subcategories, setSubcategories] = useState([]);

  // Get type from query string
  const params = new URLSearchParams(location.search);
  const type = params.get("type") || "category";
  const companyType = params.get("companyType") || "";

  // Fetch subcategories for the selected category
  useEffect(() => {
    catalogApi
      .listSubcategories(categoryId)
      .then((data) =>
        setSubcategories(Array.isArray(data?.items) ? data.items : [])
      )
      .catch(() => setSubcategories([]));
  }, [categoryId]);

  useEffect(() => {
    setLoading(true);
    setError("");
    if (type === "allsubcategories") {
      // Fetch all subcategories for this category, then fetch services for those subcategories
      catalogApi
        .listSubcategories(categoryId)
        .then(async (data) => {
          const subcategories = Array.isArray(data?.items) ? data.items : [];
          if (subcategories.length === 0) {
            setServices([]);
            setLoading(false);
            return;
          }
          const subcategoryIds = subcategories.map((sub) => sub._id).join(",");
          const query = { subcategoryIds };
          if (companyType) query.companyType = companyType;
          const listings = await catalogApi.publicSearch(query);
          setServices(Array.isArray(listings?.items) ? listings.items : []);
          setLoading(false);
        })
        .catch(() => {
          setError("Failed to load listings");
          setServices([]);
          setLoading(false);
        });
    } else if (type === "subcategory") {
      // Fetch services for a single subcategory
      catalogApi
        .publicSearch(
          companyType ? { subcategoryIds: categoryId, companyType } : { subcategoryIds: categoryId }
        )
        .then((data) =>
          setServices(Array.isArray(data?.items) ? data.items : [])
        )
        .catch(() => {
          setError("Failed to load listings");
          setServices([]);
        })
        .finally(() => setLoading(false));
    } else {
      // Default: fetch services for the category itself
      catalogApi
        .publicSearch(
          companyType ? { categoryIds: categoryId, companyType } : { categoryIds: categoryId }
        )
        .then((data) =>
          setServices(Array.isArray(data?.items) ? data.items : [])
        )
        .catch(() => {
          setError("Failed to load listings");
          setServices([]);
        })
        .finally(() => setLoading(false));
    }
  }, [categoryId, type]);

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-orange-400 via-white to-green-400">
        <div className="relative flex items-center justify-center h-24 w-24 mb-6">
          <span className="absolute inline-flex h-full w-full rounded-full bg-gradient-to-tr from-orange-500 to-green-500 opacity-30 animate-ping"></span>
          <svg
            className="animate-spin h-16 w-16 text-orange-600 drop-shadow-lg"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-20"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-80"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
        </div>
        <div className="text-2xl font-semibold text-gray-800 tracking-wide">
          Loading services...
        </div>
        <div className="text-sm text-gray-600 mt-2">
          Please wait while we fetch the best options for you.
        </div>
      </div>
    );
  if (error) return <div className="text-center text-red-500">{error}</div>;
  if (!services.length)
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-700 bg-white/60 rounded-md p-10 border border-dashed">
        <SearchX className="h-10 w-10 text-gray-400 mb-2" />
        <div className="text-lg font-medium">No services found</div>
        <div className="text-sm text-gray-500">
          Try adjusting your search or filters.
        </div>
      </div>
    );

  return (
    <>
      <section className="relative w-full h-1/2 overflow-hidden">
        {/* Background Image (with blur) */}
        <img
          src={HalfHero}
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover z-[-10] blur-sm"
        />

        {/* Navbar */}
        <Navbar />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/80 z-[-5]" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full text-white flex flex-col justify-center items-center">
          <div className="h-3/4 w-3/4 text-white flex flex-col justify-center items-center gap-5">
            <h1 className="text-xl font-bold text-center mulish-light uppercase">
              Welcome to <br />
              <span className="text-5xl mulish-bold tracking-wider bg-gradient-to-r from-orange-500 via-white to-green-500 bg-clip-text text-transparent ">
                ONLINE INDIAN MARKET
              </span>
            </h1>
            <p className="mt-2 text-sm mulish-light tracking-wider text-center uppercase">
              Get the Nearest and Best Services in the Market.
            </p>
            <SearchBar />
            <div className="flex flex-row justify-around w-3/4"></div>
          </div>
        </div>
      </section>
      <section className="flex flex-col justify-center px-10 items-center">
        <div className="w-full flex flex-col md:flex-row justify-center items-center md:items-start md:gap-6 ">
          {/* Mobile toggle button */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden px-4 py-2 bg-blue-500 text-white rounded-lg shadow mt-4"
          >
            Filters
          </button>

          {/* Sidebar: pass only subcategories of the selected category */}
          <div className="w-1/4 sticky top-0 p-0">
            {/* Sidebar */}
            <FilterSideBar
              isOpen={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
            />
          </div>

          <div className="md:w-3/4 my-10 overflow-y-auto pr-2 w-full space-y-6">
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 w-full">
              {services.map((listing) => (
                <ListingCard
                  key={listing.id || listing._id}
                  listing={listing}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesByCategory;
